import React, { useEffect } from 'react';
import { Button, LoadingButton, ModalHeader, ModalBody, ModalFooter, Typography, } from '@one-thd/sui-atomic-components';
import { setDeleteCardOverlayAnalytics } from '../../../utils/payments-analytics';
import { CardType } from '../../../constants/payments';
import { GiftCardLogoAndNumber } from '../gift-card/logo-number';
import { getCardImagePath } from '../../../utils/credit-cards';
import { IMAGES_ASSETS_URL } from '../../../constants/links';
import { getFormattedKeytag } from '../../../utils/payments';
export const ICDefaultCardMsg = "This is also your Instant Checkout card. You'll need to set up how you pay before you can shop with Instant Checkout again.";
// may eventually change type to Record<CardType ...>
const headerMappings = {
    [CardType.CREDIT_CARD]: (card, isAdmin) => {
        if (card.citiAccountId && isAdmin) {
            return 'Delete Home Depot Credit Cards';
        }
        return card.isDefault
            ? 'Do You Want To Delete Your Default Card?'
            : 'Do You Want To Delete This Card?';
    },
    [CardType.GIFT_CARD]: () => {
        return 'Do You Want To Delete This Gift Card?';
    },
    [CardType.PAYPAL_ACCOUNT]: () => 'Do You Want To Delete This PayPal Account?',
    [CardType.CHECKING_ACCOUNT]: () => 'Do You Want To Delete This Checking Account?',
    [CardType.KEY_TAG]: () => 'Do You Want To Delete This Keytag?',
};
// may eventually change type to Record<CardType ...>
const buttonMappings = {
    [CardType.CREDIT_CARD]: (card, isAdmin) => {
        if (card.citiAccountId && isAdmin) {
            return 'Yes, Delete All Cards';
        }
        return card.isDefault ? 'Yes, Delete Default Card' : 'Yes, Delete Card';
    },
    [CardType.GIFT_CARD]: () => {
        return 'Yes, Delete Gift Card';
    },
    [CardType.PAYPAL_ACCOUNT]: () => 'Yes, Delete PayPal Account',
    [CardType.CHECKING_ACCOUNT]: () => 'Yes, Delete Checking Account',
    [CardType.KEY_TAG]: () => 'Yes, Delete Keytag',
};
const childrenMapping = {
    [CardType.CREDIT_CARD]: (card, isB2B, isAdmin, paginationInfo, hasProAllowance, hasHdWallet) => {
        var _a;
        const { cardBrand, cardType, cardNumberLast4, cardExpiryMonth, cardExpiryYear, citiAccountId, instantCheckoutDefault, } = card;
        const showAdminCitiText = isAdmin && citiAccountId;
        const groupedCitiCard = paginationInfo &&
            ((_a = paginationInfo.citiCardsSummary) === null || _a === void 0 ? void 0 : _a.find((groupedCard) => groupedCard.citiAccountId === citiAccountId));
        const showProAllowanceMessage = isAdmin && hasProAllowance;
        const showHdWalletMessage = hasHdWallet;
        return (React.createElement(React.Fragment, null, showAdminCitiText ? (React.createElement(React.Fragment, null,
            React.createElement(Typography, { weight: "bold" }, "Are you sure you want to proceed? This action will remove all credit cards associated with this Citi Account ID from your Pro Xtra account."),
            React.createElement("div", { className: "sui-flex sui-gap-4 sui-mb-4" },
                React.createElement("div", { className: "sui-flex sui-gap-2" },
                    React.createElement("img", { width: 33, height: "100%", src: getCardImagePath(cardBrand, cardType), alt: "Credit card logo" }),
                    React.createElement("br", null),
                    React.createElement("br", null),
                    React.createElement(Typography, { weight: "bold" },
                        " ID",
                        citiAccountId),
                    React.createElement(Typography, null,
                        "(", groupedCitiCard === null || groupedCitiCard === void 0 ? void 0 :
                        groupedCitiCard.totalNumberOfCards,
                        " associated cards)"))),
            React.createElement("br", null),
            React.createElement(Typography, null, "If you want to reference your transaction history made with these cards, you can do so using the purchaser's account phone number."),
            React.createElement("br", null),
            React.createElement(Typography, null, "If you add these cards to another Pro Xtra account in the future, you will lose all in-store purchase history associated with these cards."))) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "sui-flex sui-gap-4 sui-mb-4" },
                React.createElement("div", { className: "sui-flex sui-gap-2" },
                    React.createElement("img", { width: 33, height: "100%", src: getCardImagePath(cardBrand, cardType), alt: "Credit card logo" }),
                    React.createElement(Typography, null,
                        " ****",
                        cardNumberLast4),
                    React.createElement(Typography, null, cardExpiryMonth &&
                        cardExpiryYear &&
                        `Exp ${cardExpiryMonth}/${cardExpiryYear.slice(-2)}`))),
            React.createElement("br", null),
            instantCheckoutDefault && (React.createElement("div", { className: "sui-mb-4" },
                React.createElement(Typography, null, ICDefaultCardMsg))),
            React.createElement(Typography, null, "You'll still be able to review all purchase history associated with this credit card in your account."),
            (showProAllowanceMessage || showHdWalletMessage) && (React.createElement("div", { className: "sui-pt-2" },
                React.createElement(Typography, null, "This action will:"),
                React.createElement("ul", { className: "sui-pt-2 sui-pl-2 sui-list-disc sui-list-inside [&_li_p]:sui-inline" },
                    showProAllowanceMessage && (React.createElement("li", null,
                        React.createElement(Typography, null, "Disable any Pro Allowances currently being charged to this card"))),
                    showHdWalletMessage && (React.createElement("li", null,
                        React.createElement(Typography, null, "Remove this card from your HD Pass"))))))))));
    },
    [CardType.GIFT_CARD]: (card) => {
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "sui-flex sui-flex-wrap sui-mb-12" },
                React.createElement(GiftCardLogoAndNumber, { card: card })),
            React.createElement(Typography, { variant: "body-base" }, "You'll still be able to review all purchase history associated with this gift card in your account.")));
    },
    [CardType.PAYPAL_ACCOUNT]: (card) => {
        const { paypalAccountId } = card;
        return (React.createElement("div", { className: "sui-w-full" },
            React.createElement("div", { className: "sui-flex sui-mb-6 sui-items-center sui-flex-wrap sui-w-full" },
                React.createElement("img", { alt: "paypal logo", width: 57, height: "100%", className: "sui-inline-block sui-mr-4", src: `${IMAGES_ASSETS_URL}/paypal-logo-v2.svg` }),
                React.createElement("div", { className: "sui-overflow-hidden sui-w-[200px] sm:sui-w-auto" },
                    React.createElement(Typography, { truncate: true, variant: "body-lg", color: "subtle" }, paypalAccountId))),
            React.createElement("br", null),
            React.createElement(Typography, null, "You'll still be able to review all purchase history associated with this PayPal Account in your account.")));
    },
    [CardType.CHECKING_ACCOUNT]: (card) => {
        const { cardNumberLast4 } = card;
        return React.createElement(Typography, null,
            "Account ****",
            cardNumberLast4);
    },
    [CardType.KEY_TAG]: (card) => {
        return (React.createElement("div", { className: "sui-pb-6" },
            React.createElement(Typography, { variant: "body-lg" }, getFormattedKeytag(card))));
    },
};
export const DeleteCardModal = ({ card, cardType, hideModal, onConfirmDelete, isLoading, isB2B, paginationInfo, isAdmin, hasProAllowance, hasHDWallet, }) => {
    const headerText = headerMappings[cardType](card, isAdmin);
    const buttonText = buttonMappings[cardType](card, isAdmin);
    const children = childrenMapping[cardType](card, isB2B, isAdmin, paginationInfo, hasProAllowance, hasHDWallet);
    useEffect(() => {
        if (card.citiAccountId && isAdmin) {
            setDeleteCardOverlayAnalytics();
        }
    }, [card.citiAccountId, isAdmin]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ModalHeader, { onClose: hideModal }, headerText),
        React.createElement(ModalBody, null, children),
        React.createElement(ModalFooter, { position: "end" },
            React.createElement("div", { className: "sui-flex sui-gap-4 sm:sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row sm:sui-w-auto" },
                React.createElement(Button, { variant: "secondary", onClick: hideModal }, "No"),
                React.createElement(LoadingButton, { loading: isLoading, variant: "primary", onClick: onConfirmDelete, "data-testid": `${cardType}-${card.isDefault ? 'default' : 'add'}-delete-confirm-btn` }, buttonText)))));
};
DeleteCardModal.displayName = 'DeleteCardModal';
